import { datadogRum } from '@datadog/browser-rum';
import { DeepValues } from '@/types/type-helpers';
import User from '@/types/user';

export const DATADOG_ACTIONS = {
  RISK_PROFILE: {
    CREATE_QUESTIONNAIRE: {
      COMPLETE_QUESTIONNAIRE: 'risk_profile.create_questionnaire.complete_questionnaire',
    },
    COMPLETE_QUESTIONNAIRE: {
      CONFIRM: 'risk_profile.complete_questionnaire.confirm',
    },
    SEND_QUESTIONNAIRE: {
      AUTHORIZE_EMAIL: 'risk_profile.send_questionnaire.authorize_email',
      COPY_LINK: 'risk_profile.send_questionnaire.copy_link',
    },
  },
} as const;

export type DatadogActionName = DeepValues<typeof DATADOG_ACTIONS>;

const init = () => {
  if (import.meta.env.REACT_APP_DATADOG_ENABLED === 'true') {
    datadogRum.init({
      applicationId: import.meta.env.REACT_APP_DATADOG_APPLICATION_ID!,
      clientToken: import.meta.env.REACT_APP_DATADOG_CLIENT_TOKEN!,
      site: import.meta.env.REACT_APP_DATADOG_SITE,
      service: 'connect-react',
      env: import.meta.env.REACT_APP_SENTRY_ENV,
      version: import.meta.env.REACT_APP_VERSION,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      allowedTracingUrls: [/^https:\/\/[A-Za-z.-]*\.capintel\.(com|io)/],
      trackResources: import.meta.env.REACT_APP_DATADOG_TRACK_RESOURCES === 'true',
      trackLongTasks: import.meta.env.REACT_APP_DATADOG_TRACK_LONG_TASKS === 'true',
      trackUserInteractions: import.meta.env.REACT_APP_DATADOG_TRACK_USER_INTERACTIONS === 'true',
      enablePrivacyForActionName: true,
      defaultPrivacyLevel: 'allow',
    });
  }
};

const identify = (user: User) => {
  datadogRum.setUser({
    id: user.id,
    email: user.email,
    name: user.email,
    nameId: user.nameId,
    enterpriseId: user.enterprise?._id,
    enterpriseName: user.enterprise?.name,
    permissions: user.permissions,
    authUserId: user.authUser.id,
    authUserEmail: user.authUser.email,
  });
};

const resetIdentity = () => {
  datadogRum.clearUser();
  datadogRum.stopSession();
};

const shutdown = () => {
  // Stops data collection and ends the session
  datadogRum.setTrackingConsent('not-granted');
};

const getSessionURL = () => datadogRum.getSessionReplayLink();

/**
 * Manually tracks a custom user action in Datadog RUM.
 * @param name - The name of the action to track
 * @param context - Optional additional context to attach to the action
 */
const addAction = (name: DatadogActionName, context?: Record<string, unknown>) => {
  datadogRum.addAction(name, context);
};

export default {
  init,
  identify,
  resetIdentity,
  shutdown,
  getSessionURL,
  addAction,
};
